import { type Fingering } from '../guitar/library.js'

export enum Duration {
  QuarterNote = 'q',
  HalfNote = 'h',
  WholeNote = 'w',
  EightsNote = '8',
  SixteenthNote = '16',
}

export enum NoteFinish {
  Vibrato = 'v',
  StrongVibrato = 'V',
}

export enum InstrumentType {
  Guitar = 'guitar',
  Bass = 'bass',
}

/** https://en.wikipedia.org/wiki/ASCII_tab */
export interface TabStep {
  fingering: Fingering | Fingering[]

  duration?: Duration
  // articulation?: Articulation
  finish?: NoteFinish
  /** This step begins a new measure — a `|` stood at this column on every string. */
  startsMeasure?: boolean
}

export interface Tab {
  name?: string
  /** Tablang string */
  lang?: string
  /** Instrument type (guitar or bass) - defaults to guitar for backward compatibility */
  instrument?: InstrumentType
  /**
   * The captured ASCII, verbatim. When present this is the source of truth and `steps` is derived
   * from it. Never written by a serializer — see spec 2026-08-25 §5. Optional: tabs created before
   * this field existed, and tabs built from Tablang, have no ASCII source to record.
   */
  source?: string
  steps: TabStep[]
}

const guitarStringLabels = ['e', 'B', 'G', 'D', 'A', 'E']
const bassStringLabels = ['G', 'D', 'A', 'E']

export const tabToAscii = (tab: Tab) => {
  /** Map out the tab in a sequence. The sequence is sequence[string][sequenceNo] */
  const isBass = tab.instrument === InstrumentType.Bass
  const stringCount = isBass ? 4 : 6
  const stringLabels = isBass ? bassStringLabels : guitarStringLabels
  const sequence: (number | string)[][] = Array.from({ length: stringCount }, () => [])
  const tabSteps = tab.steps

  for (let stepIndex = 0; stepIndex < tabSteps.length; stepIndex++) {
    const tabStep = tabSteps[stepIndex]

    if (tabStep.startsMeasure && stepIndex > 0) {
      // Not on the first step: the leading `|` is already written by the label prefix.
      for (let str = 0; str < stringCount; str++) {
        sequence[str].push('|')
      }
    }

    const stringNums: number[] = Array.from({ length: stringCount }, (_, i) => i)
    const isLastStep = stepIndex === tabSteps.length - 1

    const fingerings = Array.isArray(tabStep.fingering) ? tabStep.fingering : [tabStep.fingering]

    if (fingerings.length > 0) {
      // Handle regular fingering
      /** @todo when one of the is double digit, we need to use '--' for the muted strings, or 7- for the single digit strings */
      const maxFret = Math.max(...fingerings.map(f => f.fret as number))
      const hasDoubleDigit = maxFret >= 10
      const hasArticulation = fingerings.some(f => f.articulation)

      for (const str of stringNums) {
        const fingering = fingerings.find(f => f.str === str + 1)
        if (fingering?.fret !== undefined && fingering.fret !== null) {
          // Add fret number
          /**
           * Ghost notes keep their parens and vibrato keeps its `~`. Spec section 9: information the
           * parser newly captures must survive a render, or a tab edited through the ASCII view
           * silently loses it — which is worse than never having captured it.
           */
          const fretStr = fingering.ghost ? `(${fingering.fret})` : fingering.fret.toString()
          for (const char of fretStr) {
            sequence[str].push(char)
          }
          if (tabStep.finish === NoteFinish.Vibrato) {
            sequence[str].push('~')
          } else if (tabStep.finish === NoteFinish.StrongVibrato) {
            sequence[str].push('V')
          }
          // Add articulation if present
          if (fingering.articulation) {
            sequence[str].push(fingering.articulation)
          } else if (hasArticulation) {
            // No articulation on this fingering, but other strings have articulations
            // Add a padding dash to maintain alignment
            sequence[str].push('-')
          }
          // Pad with dash if needed for alignment with double-digit frets
          if (hasDoubleDigit) {
            const totalLength = fretStr.length + (fingering.articulation ? 1 : 0)
            // Double digit frets are 2 chars, or 3 with articulation
            const targetLength = fingering.articulation ? 3 : 2
            for (let i = totalLength; i < targetLength; i++) {
              sequence[str].push('-')
            }
          }
        } else if (fingering && fingering.fret === null) {
          // A muted string is `x`, not a dash — it is a played note, not an absence.
          sequence[str].push('x')
          if (hasDoubleDigit) {
            sequence[str].push('-')
          }
          if (hasArticulation) {
            sequence[str].push('-')
          }
        } else {
          // No fingering on this string - add dashes to match the width of strings with fingerings
          sequence[str].push('-')
          if (hasDoubleDigit) {
            sequence[str].push('-')
          }
          if (hasArticulation) {
            sequence[str].push('-') // Extra dash for articulation space
          }
        }
      }

      // Add trailing dash after each note step, except for the last step
      if (!isLastStep) {
        for (const str of stringNums) {
          sequence[str].push('-')
        }
      }
    } else {
      // Handle pause steps
      if (stepIndex === 0) {
        // First step is a pause - add two dashes (no previous trailing dash)
        for (const str of stringNums) {
          sequence[str].push('-')
          sequence[str].push('-')
        }
      } else {
        // Subsequent pause steps - add one dash
        // (the trailing dash from previous note steps provides the spacing)
        for (const str of stringNums) {
          sequence[str].push('-')
        }
      }
    }
  }

  // Check if this tab contains any pause steps (empty fingering arrays)
  const hasPauses = tab.steps.some(step => {
    const fingerings = Array.isArray(step.fingering) ? step.fingering : [step.fingering]
    return fingerings.length === 0
  })

  // Use different formats based on whether we have pauses
  if (hasPauses) {
    // For tabs with pauses, use format compatible with ASCII parser: |content|
    return sequence.map((s, idx) => `${stringLabels[idx]} |${s.join('')}|`)
  } 
    // For tabs without pauses, use original format: |-content-|
    return sequence.map((s, idx) => `${stringLabels[idx]} |-${s.join('')}-|`)
  
}

/**
 * The text to show for a tab. `verbatim` is false when there was no stored source and the text is a
 * reconstruction from `steps` — which has normalised column spacing and therefore no longer carries
 * the rhythm signal `tab-rhythm.ts` reads. Callers must not present a reconstruction as the
 * original.
 */
export const tabSourceText = (tab: Tab): { text: string; verbatim: boolean } =>
  tab.source !== undefined
    ? { text: tab.source, verbatim: true }
    : { text: tabToAscii(tab).join('\n'), verbatim: false }
